import type { APIGatewayProxyEvent } from "aws-lambda";

// Fee types

export type FeeType = "NEW" | "USED";

// Anything else (e.g. the retired "ALL") is ignored wherever fees are read or written.
export const isFeeType = (value: unknown): value is FeeType =>
  value === "NEW" || value === "USED";

export interface Fee {
  id?: string;
  name: string;
  amount: number | null;
  removed: boolean;
  type: FeeType;
}

// API Request/Response types

export interface GetFeesRequest {
  tenantType: string;
  tenantId: string;
}

export interface GetFeesResponse {
  fees: Fee[];
}

export interface SaveFeesRequest {
  tenantType: string;
  tenantId: string;
  fees: Fee[];
  tenantUser: string;
  clientId?: string;
  // Replaces only this type's fees in the bundle; the bundle's other fees are kept.
  vehicleType?: FeeType;
}

export type SaveFeesResponse =
  | { success: true; fees: Fee[] }
  | { success: false; error: "CONFLICT" };

// Config types

export type PriceAfterFeesAttribute =
  | "askingPrice"
  | "internetPrice"
  | "invoicePrice"
  | "msrp"
  | "retailValue"
  | "salePrice"
  | "wholesalePrice";

export interface FeeManagerConfig {
  NEW_applyOnInventoryItemSave: boolean;
  NEW_saveTotalFees: boolean;
  NEW_saveItemizedFees: boolean;
  NEW_savePriceAfterFees: boolean;
  NEW_priceAfterFeesInputAttribute: PriceAfterFeesAttribute;
  USED_applyOnInventoryItemSave: boolean;
  USED_saveTotalFees: boolean;
  USED_saveItemizedFees: boolean;
  USED_savePriceAfterFees: boolean;
  USED_priceAfterFeesInputAttribute: PriceAfterFeesAttribute;
  // "common_org" (any case) reads and writes that type's fees on the common org bundle; missing means "ddc".
  NEW_feeSource?: string;
  USED_feeSource?: string;
  [key: string]: unknown;
}

export type SaveConfigResponse =
  | { success: true; config: FeeManagerConfig }
  | { success: false; error: "CONFLICT" };

export interface GetConfigRequest {
  tenantType: string;
  tenantId: string;
}

export interface SaveConfigRequest {
  tenantType: string;
  tenantId: string;
  config: FeeManagerConfig;
  tenantUser: string;
  clientId?: string;
}

// Injectable persistence seam for Fee Manager configuration. The Lambda handler
// (core-feemanager-lambda) routes GET/POST /config through a provider: DDC uses the
// default Inventory-API provider; Autotrader Dealer Site injects an owner-product
// provider. Defined in the interface layer so both the core lambda and the Autotrader
// provider lib can depend on it without importing each other (DDC↔Autotrader firewall).
export interface FeeManagerConfigProvider {
  getConfig: (
    event: APIGatewayProxyEvent,
    tenantType: string,
    tenantId: string
  ) => Promise<FeeManagerConfig>;
  saveConfig: (
    event: APIGatewayProxyEvent,
    tenantType: string,
    tenantId: string,
    config: FeeManagerConfig,
    tenantUser: string,
    clientId?: string
  ) => Promise<SaveConfigResponse>;
}

// Configuration schema types

type FeeManagerConfigKnownFields = Pick<
  FeeManagerConfig,
  | "NEW_applyOnInventoryItemSave"
  | "NEW_saveTotalFees"
  | "NEW_saveItemizedFees"
  | "NEW_savePriceAfterFees"
  | "NEW_priceAfterFeesInputAttribute"
  | "USED_applyOnInventoryItemSave"
  | "USED_saveTotalFees"
  | "USED_saveItemizedFees"
  | "USED_savePriceAfterFees"
  | "USED_priceAfterFeesInputAttribute"
>;

export type FeeManagerConfigFieldKey = keyof FeeManagerConfigKnownFields;

export type FeeManagerConfigFieldSchema<TValue> =
  | {
      type: "boolean";
      defaultValue: boolean;
      label: string;
    }
  | {
      type: "select";
      defaultValue: TValue;
      label: string;
      options: { label: string; value: TValue }[];
      disabled?: (config: FeeManagerConfig) => boolean;
    }
  | {
      type: "input";
      inputType: "text" | "number";
      defaultValue: TValue;
      label: string;
    };

// Subset-capable: a tenant schema MAY omit fields (e.g. Autotrader Dealer Site
// omits the price-after-fees input attribute). Consumers iterate Object.keys(schema),
// so an absent key is simply neither rendered nor part of that tenant's config contract.
export type FeeManagerTenantSchema = Partial<{
  [TKey in FeeManagerConfigFieldKey]: FeeManagerConfigFieldSchema<
    FeeManagerConfigKnownFields[TKey]
  >;
}>;

export const getConfigDefaults = (
  schema: FeeManagerTenantSchema
): FeeManagerConfig =>
  Object.fromEntries(
    (Object.keys(schema) as FeeManagerConfigFieldKey[]).map(key => [
      key,
      schema[key]?.defaultValue
    ])
  ) as FeeManagerConfig;

export const DEFAULT_FEE_MANAGER_CONFIG: FeeManagerConfig = {
  NEW_applyOnInventoryItemSave: false,
  NEW_saveTotalFees: false,
  NEW_saveItemizedFees: false,
  NEW_savePriceAfterFees: false,
  NEW_priceAfterFeesInputAttribute: "internetPrice",
  USED_applyOnInventoryItemSave: false,
  USED_saveTotalFees: false,
  USED_saveItemizedFees: false,
  USED_savePriceAfterFees: false,
  USED_priceAfterFeesInputAttribute: "internetPrice"
};

export const coerceSubTogglesToMaster = (
  config: FeeManagerConfig
): { config: FeeManagerConfig; wasCoerced: boolean } => {
  const newMaster = config.NEW_applyOnInventoryItemSave;
  const usedMaster = config.USED_applyOnInventoryItemSave;

  const wasCoerced =
    config.NEW_saveTotalFees !== newMaster ||
    config.NEW_saveItemizedFees !== newMaster ||
    config.NEW_savePriceAfterFees !== newMaster ||
    config.USED_saveTotalFees !== usedMaster ||
    config.USED_saveItemizedFees !== usedMaster ||
    config.USED_savePriceAfterFees !== usedMaster;

  if (!wasCoerced) return { config, wasCoerced: false }; // same reference — callers treat it as read-only

  return {
    config: {
      ...config,
      NEW_saveTotalFees: newMaster,
      NEW_saveItemizedFees: newMaster,
      NEW_savePriceAfterFees: newMaster,
      USED_saveTotalFees: usedMaster,
      USED_saveItemizedFees: usedMaster,
      USED_savePriceAfterFees: usedMaster
    },
    wasCoerced: true
  };
};

// The only keys a config file may hold; anything else is ignored on read and never written.
const FEE_MANAGER_CONFIG_KEYS = new Set<string>([
  ...Object.keys(DEFAULT_FEE_MANAGER_CONFIG),
  "NEW_feeSource",
  "USED_feeSource"
]);

export const pickConfigFields = (
  raw: Record<string, unknown>
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(raw).filter(([key]) => FEE_MANAGER_CONFIG_KEYS.has(key))
  );

export const normalizeFeeManagerConfig = (
  raw: Record<string, unknown>
): { config: FeeManagerConfig; isNormalized: boolean } => {
  const fields = pickConfigFields(raw);
  const hadMissingProperties = Object.keys(DEFAULT_FEE_MANAGER_CONFIG).some(
    key => !(key in fields)
  );
  const { config, wasCoerced } = coerceSubTogglesToMaster({
    ...DEFAULT_FEE_MANAGER_CONFIG,
    ...fields
  } as FeeManagerConfig);
  return { config, isNormalized: hadMissingProperties || wasCoerced };
};

// Fetch interface for hooks

export interface FeeManagerFetchInterface {
  tenantType: string;
  tenantId: string;
  disabled?: boolean;
  // Extra query params on every request, e.g. DDC's asAccount/asUser for the lambda's tenant guard.
  requestParams?: Record<string, string>;
}

export interface FeeManagerFetchWriteInterface {
  tenantType: string;
  tenantId: string;
  afterSuccess?: ((data: unknown) => void) | string;
  clientId?: string;
  requestParams?: Record<string, string>;
}

// Type guards

export function isFee(object: unknown): object is Fee {
  if (!object || typeof object !== "object") return false;
  const obj = object as Record<string, unknown>;
  return (
    (obj.id === undefined || typeof obj.id === "string") &&
    typeof obj.name === "string" &&
    (typeof obj.amount === "number" || obj.amount === null) &&
    typeof obj.removed === "boolean" &&
    (obj.type === "NEW" || obj.type === "USED")
  );
}

export function isGetFeesResponse(object: unknown): object is GetFeesResponse {
  if (!object || typeof object !== "object") return false;
  const obj = object as Record<string, unknown>;
  return Array.isArray(obj.fees);
}
